import * as React from "react";
import WishlistContext from "../services/WishlistContext";
import WishlistService from "../services/WishlistService";
import { IWishlistItem } from "../models/IWishlistItem.model";
import Helpers from "../../../gulp/scripts/typescript/modules/helpers";

export interface IQuantityPickerProps extends IWishlistItem {
	setLoading: (loading: boolean)=> void;
	isLoading: boolean;
}

function QuantityPicker(props: IQuantityPickerProps) {

	const [errorMessage, setErrorMessage] = React.useState("");
	const [quantity, setQuantity] = React.useState(props.Quantity);
	const [hasLoaded, setHasLoaded] = React.useState(false);
	const wishlistContext = React.useContext(WishlistContext);
	const wishlistService = new WishlistService();
	const [debouncer, setDebouncer] = React.useState(null);
	const maxQuantity = 50;

	function debounce(f: any, duration: number) {
		clearTimeout(debouncer);
		setDebouncer(setTimeout(f, duration));
	}

	function setWishlistSummaryCount(quantity: number) {
		const updateWishlistSummaryCount = Helpers.createCustomEventForDispatch('updateWishlistSummaryCount', {
			count: quantity
		});
		document.dispatchEvent(updateWishlistSummaryCount);
		if (!quantity) {
			const shareWishlist = document.querySelector('.js-wishlist-share');
			if (shareWishlist) {
				shareWishlist.setAttribute('aria-hidden', 'true');
			}
		}
	}

	React.useEffect(() => {
		setHasLoaded(false);
		setQuantity(props.Quantity);
		checkValueForErrors(props.Quantity);
		setHasLoaded(true);
	}, [props.Quantity]);

	React.useEffect(() => {
		if(!hasLoaded) {
			setHasLoaded(true);
			checkValueForErrors(quantity);
			return;
		}
		debounce(() => {
			if(errorMessage) {
				return;
			}
			props.setLoading(true);
			wishlistService.updateWishlistItem(props.LineItemGuid, quantity).then((data) => {
				setWishlistSummaryCount(data._embedded.wishlist.TotalItemQuantity);
				wishlistContext.setWishlist(data._embedded.wishlist);
				props.setLoading(false);
			})
			.catch(()=> {
				setErrorMessage("An error has occurred. Please try again.");
				props.setLoading(false);
			});

		}, 500);

  }, [quantity]);

	function handleOnChange(e: React.FormEvent<HTMLInputElement>) {
		let nextQuantity = parseInt(e.currentTarget.value);
		checkValueForErrors(nextQuantity);
		if(isNaN(nextQuantity)) {
			nextQuantity = null;
		}
		if(nextQuantity === 0) {
			e.currentTarget.blur();
		}
		setQuantity(nextQuantity);
	}

	function decreaseQuantity(e: React.MouseEvent) {
		const nextQuantity = quantity - 1;
		checkValueForErrors(nextQuantity);
		setQuantity(quantity - 1);
	}

	function increaseQuantity(e: React.MouseEvent) {
		const nextQuantity = quantity + 1;
		checkValueForErrors(nextQuantity);
		setQuantity(nextQuantity);
	}

	function checkValueForErrors(value: number) {
		if(isNaN(value)) {
			setErrorMessage("Please enter a quantity");
			return false;
		}
		if (value > maxQuantity) {
			setErrorMessage(`${maxQuantity} is the maximum quantity`);
			return false;
		}
		setErrorMessage("");
		return true;
	}

	function handleFormSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
	}

	return (
		<form className="c-add-product c-add-product--active u-mb10" onSubmit={(e)=>handleFormSubmit(e)}>
			<div>
				<label className="c-add-product__label u-visuallyhidden" htmlFor={`wishlist-quantity-${props.Product.Guid}`}>Quantity</label>
				<div className="c-add-product__controls c-add-product--slim">
					<button className="c-button c-button--full c-button--secondary c-add-product__controls__less"
					onClick={(e)=>decreaseQuantity(e)}
					type="button" data-test="basket-product-decrease-quantity"
									disabled={quantity === 0 || !quantity || props.isLoading}>
						<span className="u-visuallyhidden">Remove one {props.Product.DisplayName} from your wishlist</span>
					</button>
					<input type="tel" disabled={props.isLoading} name={`quantity-${props.Product.Guid}`} id={`wishlist-quantity-${props.Product.Guid}`} className="c-add-product__controls__text" value={quantity} onChange={(e) => handleOnChange(e)} pattern="^[0-9]*$" data-value-before-change={quantity} data-min="1" data-max={maxQuantity} maxLength={2} minLength={1} data-test="basket-item-quantity" />
					<button className="c-button c-button--full c-button--secondary c-add-product__controls__more" type="button" data-test="basket-product-increase-quantity"
					onClick={(e)=>increaseQuantity(e)}
						disabled={quantity >= maxQuantity || props.isLoading}>
						<span className="u-visuallyhidden">Add one {props.Product.DisplayName} to your wishlist</span>
					</button>
				</div>
				{errorMessage?
					<p className="c-add-product__message-error" data-test="basket-product-item-message">
						{errorMessage}
					</p>
					: null}
				<input type="hidden" name="id" value={props.Product.Name} data-test="product-in-basket-id" />
				<input type="hidden" name="guid" value={props.Product.Guid} data-test="product-in-basket-guid" />
			</div>
		</form>
	);


}

export default QuantityPicker;
